import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch } from 'react-router';
import { ListToolbar } from '@/components/list/ListToolbar';
import { useListParams } from '@/components/list/use-list-params';
import { cn } from '@/lib/utils';
import { usePlaceContext, type PlaceContext } from '../place/PlaceLayout';
import { sectionPath } from '../place/place';
import { useRoleText } from '../roles/role-text';
import { PageHead } from '../ui/PageHead';
import { CatalogListPane } from './CatalogListPane';
import { useRoleCatalog, type RoleCatalogEntry } from './use-role-catalog';

export { assignedBy } from './CatalogListPane';

/** The platform's own roles have no module; they are listed, and filtered, as «Amministrazione». */
const PLATFORM_GROUP = 'administration';
const SCOPES = ['global', 'tenant', 'organization', 'department', 'group', 'project'] as const;
type Scope = (typeof SCOPES)[number];

type Filters = { readonly q: string; readonly module: string | null; readonly scope: Scope | null };

function readFilters(params: URLSearchParams): Filters {
  const scope = params.get('scope');
  return {
    q: params.get('q') ?? '',
    module: params.get('module'),
    scope: SCOPES.find((value) => value === scope) ?? null,
  };
}

const FILTER_KEYS = ['module', 'scope'] as const;

export const groupOf = (entry: RoleCatalogEntry): string => entry.module?.code ?? PLATFORM_GROUP;

/** What the catalog hands its role card (`RoleSheet`): the roles as filtered now. */
export type RoleCatalogContext = PlaceContext & {
  readonly items: readonly RoleCatalogEntry[];
  readonly loading: boolean;
};

function useCatalogView(items: readonly RoleCatalogEntry[], filters: Filters) {
  const { t, i18n } = useTranslation('administration');
  const { name, description } = useRoleText();
  // A module the console has no words for yet keeps the catalog's own name, as roles do.
  const moduleLabel = useCallback(
    (code: string, fallback: string) => {
      const key = `administration:modules.${code}`;
      return i18n.exists(key) ? t(key as never) : fallback;
    },
    [t, i18n],
  );
  const modules = useMemo(() => {
    const seen = new Map<string, string>();
    for (const entry of items) {
      const code = groupOf(entry);
      if (!seen.has(code)) {
        seen.set(code, moduleLabel(code, entry.module?.name ?? code));
      }
    }
    return [...seen.entries()];
  }, [items, moduleLabel]);
  const shown = useMemo(() => {
    const q = filters.q.trim().toLocaleLowerCase();
    return items.filter(
      (entry) =>
        (filters.module === null || groupOf(entry) === filters.module) &&
        (filters.scope === null || entry.scope === filters.scope) &&
        (q === '' ||
          name(entry).toLocaleLowerCase().includes(q) ||
          (description(entry) ?? '').toLocaleLowerCase().includes(q)),
    );
  }, [items, filters, name, description]);
  const groups = useMemo(() => {
    const byGroup = new Map<string, RoleCatalogEntry[]>();
    for (const entry of shown) {
      const code = groupOf(entry);
      byGroup.set(code, [...(byGroup.get(code) ?? []), entry]);
    }
    return [...byGroup.entries()];
  }, [shown]);
  return { moduleLabel, modules, shown, groups };
}

function CatalogHead({
  titleId,
  filters,
  modules,
  list,
}: {
  readonly titleId: string;
  readonly filters: Filters;
  readonly modules: readonly (readonly [string, string])[];
  readonly list: ReturnType<typeof useListParams>;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('roleCatalog.title')}
        summary={t('roleCatalog.summary')}
      />
      {/* The shared filterable list (decision 101): search, filters as labels. */}
      <ListToolbar
        label={t('roleCatalog.title')}
        search={{
          label: t('roleCatalog.searchLabel'),
          value: filters.q,
          onWrite: (q) => list.set('q', q),
        }}
        fields={[
          {
            key: 'module',
            label: t('roleCatalog.moduleFilter'),
            options: modules.map(([code, label]) => ({ value: code, label })),
          },
          {
            key: 'scope',
            label: t('roleCatalog.scopeFilter'),
            options: SCOPES.map((scope) => ({
              value: scope,
              label: t(`roleCatalog.scope.${scope}`),
            })),
          },
        ]}
        active={list.active}
        onFilter={list.set}
        onClear={list.clear}
      />
    </div>
  );
}

/**
 * Catalogo ruoli (FR-AD-5, the third round of boards in decision 61): every role of the console in
 * plain words — what it allows, where it is granted, and who may grant it. It is read only, at
 * every place, for everyone who administers it. The roles arrive in the server's order, grouped
 * by module; the search and the two filters work on that list here, and live in the address like
 * the other lists' filters.
 */
export function RoleCatalogPage() {
  const { place } = usePlaceContext();
  const { state, reload } = useRoleCatalog(place);
  const context = usePlaceContext();
  const list = useListParams(FILTER_KEYS);
  const filters = useMemo(() => readFilters(list.params), [list.params]);
  const titleId = useId();
  const listPath = sectionPath(place, 'role-catalog');
  const sheetOpen = useMatch(`${listPath}/:roleId`) !== null;
  const query = list.params.size > 0 ? `?${list.params.toString()}` : '';
  const clearFilters = list.clear;

  const items = state.status === 'ready' ? state.items : [];
  const { moduleLabel, modules, shown, groups } = useCatalogView(items, filters);

  const childContext: RoleCatalogContext = {
    ...context,
    items: shown,
    loading: state.status === 'loading',
  };

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section flex flex-col gap-5', sheetOpen && 'people-section-with-sheet')}
    >
      <CatalogHead titleId={titleId} filters={filters} modules={modules} list={list} />
      <div className={cn('people-layout', sheetOpen && 'people-layout-with-sheet')}>
        <div className="people-list-pane">
          <CatalogListPane
            state={state}
            shown={shown}
            groups={groups}
            moduleLabel={moduleLabel}
            reload={reload}
            onClear={clearFilters}
            hrefOf={(entry) => `${listPath}/${entry.id}${query}`}
          />
        </div>
        <Outlet context={childContext} />
      </div>
    </section>
  );
}
